// Note loading strategy.
//
// The sync used to fetch every note's full body. For one account that was
// 2.3MB, of which 93% was note.content and most of that was base64 images. On a
// link with the round-trip time of Indonesia to Europe it could not finish
// inside the socket client's timeout, so the notes list simply never appeared.
//
// Now the list is fetched as metadata only and a body is loaded when its note
// is opened. feathers-vuex merges records key by key (replaceItems is false),
// so a metadata fetch never strips a body that is already in the store.

// Everything the list and the note header render.
//
// _id must be listed explicitly. The server would return it regardless, but
// feathers-vuex re-applies $select when reading back out of the store, and
// adapter-commons implements that as _.pick(result, ...fields) with no id field
// appended. Leave _id out and every note in the list comes back without one,
// so selecting a note routes to /app/notes/<folder>/undefined.
export const NOTE_LIST_FIELDS = [
  '_id',
  'title',
  'excerpt',
  'folderId',
  'ownerId',
  'archived',
  'pinned',
  'pinnedOrder',
  'taskSync',
  'endDate',
  'createdAt',
  'updatedAt'
]

// instanceDefaults sets content to null, so null means "not fetched yet" and
// '' means "fetched, and empty". Everything that reads or writes a body has to
// tell those apart: treating not-fetched as empty is how you overwrite a note
// with nothing.
export function hasContent(note) {
  return !!note && typeof note.content === 'string'
}

// Resolves once the note's body is in the store. Safe to call repeatedly.
export async function ensureContent(Model, noteId) {
  if (!noteId) return null
  const note = Model.getFromStore(noteId)
  if (hasContent(note)) return note
  return Model.get(noteId)
}

// Task state lives twice: as a task record, and as a block inside the note's
// HTML. Changing a task has to rewrite its block in the note. Every caller used
// to inline this, reading note.content straight out of the store -- which is
// empty until the note is opened, so the edits would silently stop syncing.
// Fetching the body first is what keeps the two copies in step.
export async function patchNoteBlock(Model, noteId, taskId, mutate) {
  if (!noteId || !taskId) return null
  const note = await ensureContent(Model, noteId)
  if (!hasContent(note)) return null

  const doc = new DOMParser().parseFromString(note.content, 'text/html')
  const block = doc.querySelectorAll(`[data-block-id="${taskId}"]`)[0]
  if (!block) return null

  mutate(block, doc)
  note.content = doc.activeElement.innerHTML
  return note.patch()
}

const DATA_URI = /data:image\/(png|jpeg|gif|webp);base64,[A-Za-z0-9+/=]+/g
const MIME = { png: 'image/png', jpeg: 'image/jpeg', gif: 'image/gif', webp: 'image/webp' }

function dataUriToBlob(uri) {
  const [meta, b64] = uri.split(',')
  const type = meta.slice(5).split(';')[0]
  const bin = atob(b64)
  const bytes = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
  return new Blob([bytes], { type })
}

// Replaces inline base64 images in note HTML with URLs to uploaded files.
// Called before a body is saved, so it catches an image however it arrived --
// pasted, dropped, or typed into the toolbar's URL prompt -- rather than
// fighting the editor's own paste handling.
//
// An upload that fails leaves that image as a data URI. The note still saves
// with the picture in it, which is worse for sync but better than losing it.
export async function externalizeImages(html, accessToken) {
  if (typeof html !== 'string' || !html.includes('data:image/')) return html
  const matches = [...new Set(html.match(DATA_URI) || [])]
  if (!matches.length) return html

  let out = html
  for (const uri of matches) {
    try {
      const blob = dataUriToBlob(uri)
      const res = await fetch('/uploads', {
        method: 'POST',
        headers: {
          'Content-Type': MIME[uri.slice(11).split(';')[0]] || blob.type,
          Authorization: `Bearer ${accessToken}`
        },
        body: blob
      })
      if (!res.ok) throw new Error(`upload failed: ${res.status}`)
      const { url } = await res.json()
      out = out.split(uri).join(url)
    } catch (e) {
      console.warn('image upload failed, keeping it inline', e.message)
    }
  }
  return out
}
